import React from 'react';
import { Box, Card, CardContent, Divider, Link, Typography } from '@mui/material';
import { UPTRADER_INVOICE_ISSUER } from '../constants/invoiceIssuer';
import { CompanyWallet } from '../services/companyWallets';

export interface InvoiceDocumentPreviewProps {
  companyName: string;
  invoiceNumber?: string;
  paymentMethod?: string;
  stripePaymentLink?: string;
  companyWallets?: CompanyWallet[];
  children?: React.ReactNode;
}

function formatCryptoWalletLine(wallet: NonNullable<CompanyWallet['wallet']>): string {
  const cur = wallet.crypto_currency || 'Crypto';
  const addr = wallet.wallet_address || '';
  const net =
    wallet.name && wallet.name !== cur ? wallet.name : '';
  return net ? `${cur} ${net} ${addr}`.trim() : `${cur} ${addr}`.trim();
}

export const InvoiceDocumentPreview: React.FC<InvoiceDocumentPreviewProps> = ({
  companyName,
  invoiceNumber,
  paymentMethod,
  stripePaymentLink,
  companyWallets = [],
  children,
}) => {
  const issuer = UPTRADER_INVOICE_ISSUER;

  const cryptoLines = companyWallets
    .filter(
      (cw) =>
        cw.wallet?.active &&
        cw.wallet.wallet_type === 'CRYPTO' &&
        cw.wallet.wallet_address
    )
    .map((cw) => formatCryptoWalletLine(cw.wallet!));

  return (
    <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, textDecoration: 'underline', mb: 2 }}
        >
          TAX INVOICE
        </Typography>

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, textDecoration: 'underline', mb: 1 }}>
            {issuer.companyName}
          </Typography>
          <Typography variant="body2">Address: {issuer.address}</Typography>
          <Typography variant="body2">TRN {issuer.trn}</Typography>
          <Typography variant="body2">
            Registration No. {issuer.registrationNo} IBAN {issuer.iban}
          </Typography>
          <Typography variant="body2">Bank Name {issuer.bankName}</Typography>
          <Typography variant="body2">Bank Country {issuer.bankCountry}</Typography>
          <Typography variant="body2">Bank Address {issuer.bankAddress}</Typography>
          <Typography variant="body2">BIC/SWIFT Code {issuer.bicSwift}</Typography>
          <Typography variant="body2">e-mail: {issuer.email}</Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            width: '100%',
            mb: 1,
          }}
        >
          <Typography variant="body2">
            <strong>To:</strong> {companyName || 'Client'}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'right' }}>
            <strong>INVOICE</strong> {invoiceNumber?.trim() || '(assigned on save)'}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, width: '100%' }}>
          <strong>Purpose of payment:</strong> Payment under UpTrader Agreement
        </Typography>

        {children}

        {(paymentMethod === 'bank_transfer' || cryptoLines.length > 0) && (
          <>
            <Divider sx={{ my: 2 }} />
            {paymentMethod === 'bank_transfer' && (
              <Box sx={{ mb: cryptoLines.length > 0 ? 1.5 : 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Pay online (Stripe):
                </Typography>
                {stripePaymentLink?.trim() ? (
                  <Link
                    href={stripePaymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {stripePaymentLink}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Payment link will be created after saving the invoice.
                  </Typography>
                )}
              </Box>
            )}
            {cryptoLines.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Crypto:
                </Typography>
                {cryptoLines.map((line, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            width: '100%',
            mt: 3,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {issuer.companyName}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
